import { useRef, useState } from "react";
import { Upload, X, Image as ImageIcon, Film, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const TIER_LIMITS = {
    verified_witness: { photo: 25, video: 100 },
    research_council: { photo: 25, video: 500 },
    admin:            { photo: 25, video: 500 },
};

function tierKey(user) {
    if (!user) return null;
    if (user.role === "admin") return "admin";
    const t = user.subscription_tier;
    return (t === "verified_witness" || t === "research_council") ? t : null;
}

/**
 * MediaUploader — paid-tier photo / video uploader.
 *
 * Props:
 *  - onUploaded(media) — invoked after a successful upload with the media record
 *  - accept            — file accept pattern (default photo + video)
 *  - testId            — base data-testid (default "media-uploader")
 *  - compact           — render as a small button instead of a drop zone
 */
export function MediaUploader({ onUploaded, accept = "image/*,video/*", testId = "media-uploader", compact = false, iconOnly = false }) {
    const { user } = useAuth();
    const tier = tierKey(user);
    const fileRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [dragOver, setDragOver] = useState(false);

    async function handleFile(file) {
        if (!file) return;
        if (!tier) {
            toast.error("Verified Witness or Research Council membership required to upload media.");
            return;
        }
        const isVideo = (file.type || "").startsWith("video/");
        const limitMB = TIER_LIMITS[tier][isVideo ? "video" : "photo"];
        if (file.size > limitMB * 1024 * 1024) {
            toast.error(`File exceeds your tier limit (${limitMB} MB)`);
            return;
        }
        const fd = new FormData();
        fd.append("file", file);
        setBusy(true);
        try {
            const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            onUploaded && onUploaded(data);
            toast.success("Media uploaded");
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed");
        } finally {
            setBusy(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    }

    function onChange(e) {
        const f = e.target.files?.[0];
        if (f) handleFile(f);
    }
    function onDrop(e) {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer?.files?.[0];
        if (f) handleFile(f);
    }

    if (!tier) {
        return (
            <div className="ok-card p-4 text-xs opacity-80" data-testid={`${testId}-locked`}>
                <div className="flex items-center gap-2">
                    <Upload className="w-4 h-4 opacity-60" />
                    <span>Photo &amp; video uploads are a <strong>Verified Witness</strong> and <strong>Research Council</strong> benefit.</span>
                    <a href="/pricing" className="ok-btn-outline text-xs ml-auto">View tiers →</a>
                </div>
            </div>
        );
    }

    if (iconOnly) {
        return (
            <>
                <button
                    type="button"
                    onClick={() => fileRef.current && fileRef.current.click()}
                    disabled={busy}
                    aria-label="Attach image"
                    className="inline-flex items-center justify-center text-[var(--ok-text-muted)] hover:text-[var(--ok-accent)] transition disabled:opacity-50"
                    style={{ width: 36, height: 36 }}
                    data-testid={`${testId}-btn`}
                >
                    {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImageIcon className="w-5 h-5" strokeWidth={1.75} />}
                </button>
                <input ref={fileRef} type="file" accept={accept} onChange={onChange} className="hidden" data-testid={`${testId}-input`} />
            </>
        );
    }

    if (compact) {
        return (
            <>
                <button
                    type="button"
                    onClick={() => fileRef.current && fileRef.current.click()}
                    disabled={busy}
                    className="ok-btn-outline text-xs inline-flex items-center gap-1"
                    data-testid={`${testId}-btn`}
                >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                    {busy ? "Uploading…" : "Attach media"}
                </button>
                <input ref={fileRef} type="file" accept={accept} onChange={onChange} className="hidden" data-testid={`${testId}-input`} />
            </>
        );
    }

    return (
        <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`ok-card p-6 text-center border-dashed transition ${dragOver ? "border-[var(--ok-accent)]" : ""}`}
            data-testid={testId}
        >
            <div className="flex items-center justify-center gap-2 mb-2 opacity-80">
                <ImageIcon className="w-4 h-4" /> <Film className="w-4 h-4" />
            </div>
            <p className="text-sm opacity-80">Drag &amp; drop a photo or video here, or</p>
            <button
                type="button"
                onClick={() => fileRef.current && fileRef.current.click()}
                disabled={busy}
                className="ok-btn-primary text-sm mt-3 inline-flex items-center gap-2"
                data-testid={`${testId}-btn`}
            >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {busy ? "Uploading…" : "Choose file"}
            </button>
            <input ref={fileRef} type="file" accept={accept} onChange={onChange} className="hidden" data-testid={`${testId}-input`} />
            <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mt-3">
                Photos up to {TIER_LIMITS[tier].photo} MB · Videos up to {TIER_LIMITS[tier].video} MB
            </div>
        </div>
    );
}

/**
 * MediaPreview — render an uploaded media record (image or video).
 */
export function MediaPreview({ media, onRemove, className = "" }) {
    if (!media || !media.url) return null;
    const fullUrl = media.url.startsWith("http") ? media.url : `${process.env.REACT_APP_BACKEND_URL}${media.url}`;
    return (
        <div className={`relative inline-block ${className}`} data-testid={`media-preview-${media.id}`}>
            {media.media_type === "video" ? (
                <video src={fullUrl} controls className="rounded max-w-full max-h-[420px] bg-black" data-testid="media-preview-video" />
            ) : (
                <img src={fullUrl} alt={media.filename || ""} className="rounded max-w-full max-h-[420px] object-contain" data-testid="media-preview-image" />
            )}
            {onRemove && (
                <button
                    type="button"
                    onClick={onRemove}
                    aria-label="Remove media"
                    className="absolute top-1 right-1 bg-black/70 hover:bg-black text-white rounded-full p-1"
                    data-testid={`media-remove-${media.id}`}
                >
                    <X className="w-3.5 h-3.5" />
                </button>
            )}
        </div>
    );
}
